// ============================================================================
// Enums
// ============================================================================

export type IssueType =
  | "title_meta"
  | "h1"
  | "h1_missing"
  | "h1_multiple"
  | "h1_too_long"
  | "alt_text"
  | "redirect"
  | "broken_link"
  | "broken_internal"
  | "canonical"
  | "duplicate_title"
  | "page_title_same_as_h1"
  // Atomic SEO-title-tag runtime types (split out of the combined title_meta).
  // TITLE-ONLY: these edit the SEO title tag only, never the meta description.
  | "page_title_missing"
  | "page_title_over_60_chars"
  | "page_title_below_30_chars"
  | "duplicate_meta"
  // Atomic meta-description runtime types (split out of the combined title_meta).
  // META-ONLY: these edit the meta description only, never the SEO title.
  | "meta_missing"
  | "meta_description_over_155_chars"
  | "meta_description_below_70_chars"
  | "thin_content"
  | "indexability"
  // Noindexed page the detector proved is important (in sitemap + internal
  // inlinks + shallow depth). Apply flips the SEO-plugin robots meta
  // noindex -> index (Yoast/RankMath).
  | "noindex_important"
  | "slow_response"
  | "near_duplicate"
  | "orphan_page"
  | "schema_error"
  | "missing_schema"
  | "schema_validation_error"
  | "missing_og_tags"
  | "mixed_content"
  | "redirect_loop"
  | "title_too_short"
  | "serp_title_truncated"
  | "serp_meta_truncated"
  | "multiple_meta"
  | "duplicate_h1"
  | "low_readability"
  | "low_text_ratio"
  | "stale_content"
  | "broken_external"
  | "deep_page"
  | "large_image"
  | "broken_image"
  | "non_nextgen_image"
  | "large_page"
  | "unsafe_crossorigin"
  | "nofollow_internal"
  | "low_internal_links"
  | "missing_hsts"
  | "hreflang_error"
  | "sitemap_error"
  // The INVERSE of sitemap_error: a live (200), indexable, internally linked
  // page that is NOT listed in the XML sitemap. Recommendation-only — the fix
  // depends on WHY it is excluded (SEO-plugin setting, exclusion filter, stale
  // sitemap cache), so we never guess a write.
  | "missing_from_sitemap"
  | "performance_diagnostics"
  | "misspelling"
  | "render_blocking"
  | "links_to_redirect"
  | "links_to_broken"
  | "missing_compression"
  | "bad_url_structure"
  | "has_iframe"
  | "https_to_http"
  | "redirect_chain"
  | "broken_resource"
  | "canonical_to_broken"
  | "canonical_to_redirect"
  | "canonical_chain"
  | "keyword_cannibalization"
  | "image_missing_dimensions"
  | "core_web_vitals";
export type IssueSeverity = "critical" | "warning" | "info";
export type IssueStatus =
  | "pending"
  | "approved"
  | "rejected"
  | "applied"
  // Recommendation-only outcome: deterministic guidance was saved but NOTHING
  // was written to WordPress (non-auto-fixable types). Distinct from "applied"
  // so the badge never claims the live site changed.
  | "recommended"
  | "verified"
  | "failed"
  | "skipped"
  | "protected"
  | "obsolete"
  | "manual"
  // Expert resolved their manual-fix ticket BY HAND — awaiting re-crawl
  // verification. Distinct from "applied" (app-written, revertable).
  | "manual_applied";
export type LargeImageResultState =
  | "applied"
  | "skipped"
  | "protected"
  | "failed";
// Background auto-suggestion generation state (MVP). Kept separate from
// IssueStatus so the locked fix_status contract is untouched and a generation
// failure never collides with the apply Verify flow. "ready" is informational
// only — once a suggestion exists the UI shows the normal "Review fix" path.
export type GenerationStatus = "queued" | "generating" | "ready" | "failed";

// Context-aware recovery actions for a failed fix apply. Populated by the
// backend GET /technical-audit/issues/{id}/recovery-actions endpoint, which
// classifies WHY the apply failed (missing plugin, mismatched secret key, or
// invalid WordPress credentials) and returns the URLs needed to recover.
// `kind: null` means no actionable recovery — render nothing.
export type RecoveryActionKind =
  | "plugin_missing"
  | "key_mismatch"
  | "creds_invalid";

export interface RecoveryActions {
  kind: RecoveryActionKind | null;
  site_url: string | null;
  /** e.g. https://csl-stl.com/wp-admin/plugins.php */
  wp_admin_url: string | null;
  /** Signed direct-download URL; present only for plugin_missing. */
  plugin_download_url: string | null;
  /** e.g. "1.4.6"; present only for plugin_missing. */
  plugin_version: string | null;
  /** The CURRENT failure text from the DB (fresh, never stale), or null. Used to
   *  render the failed-apply banner so dialogs holding a snapshot `issue` prop
   *  don't show an old error after a retry. */
  apply_error: string | null;
}

/**
 * Source-page content-edit fix families: the issue's own page is the page we'd
 * edit, so if that page isn't publicly served there's nothing worth fixing. Kept
 * in sync with the backend `TechnicalAuditService._SOURCE_EDIT_ISSUE_TYPES`.
 * Excludes broken_internal (its dead-page fix is a redirect of the 404 page).
 */
export const SOURCE_EDIT_ISSUE_TYPES: ReadonlySet<string> = new Set([
  "broken_external",
  "links_to_broken",
  "links_to_redirect",
  "image_missing_dimensions",
]);

/** Pre-apply publish-first signal (GET …/source-publish-state). */
export interface SourcePublishState {
  /** True only for eligible (source-edit, non-terminal) issues that were checked. */
  applicable: boolean;
  /** The source page returns a definitive 404/410 — unpublished or removed. */
  source_not_public: boolean;
  /** Publish-first call-to-action copy when source_not_public, else null. */
  message: string | null;
}

/** In-app editor target for a page that maps to app-generated content. */
export interface InAppEditorTarget {
  content_id: string;
  editor_url: string;
}

/** Resolved actions for the publish-first chooser (GET …/publish-targets).
 *  Fetched on demand — resolves the WP post id server-side. */
export interface PublishTargets {
  /** WordPress post/page was located (id + editor link available). */
  resolved: boolean;
  wp_post_id: number | null;
  wp_endpoint: string | null;
  /** Correct wp-admin editor deep link, or null when unresolved. */
  wp_admin_edit_url: string | null;
  /** In-app editor target, or null when the page isn't app-generated content. */
  in_app: InAppEditorTarget | null;
  /** True when the page can be published in place. */
  can_publish: boolean;
  /** Human-readable reason when nothing resolved (e.g. page deleted, WP not connected). */
  error: string | null;
}

export type TicketState = "open" | "in_progress" | "resolved";

/** An assignable expert from the Technical Audit roster. */
export interface Expert {
  id: string;
  name: string;
}

/** A manual-fix ticket (an issue handed off to the AgileCo team). */
export interface ManualFixTicket {
  issue_id: string;
  run_id: string | null;
  page_url: string | null;
  issue_type: string;
  severity: string | null;
  reason: string | null;
  state: TicketState;
  assignee: string | null;
  assignee_name: string | null;
  requested_at: string | null;
  requested_by: string | null;
  started_at: string | null;
  resolved_at: string | null;
  resolved_by: string | null;
  resolved_note: string | null;
  external_ticket_url?: string | null;
}

/** One workspace's tickets inside the cross-workspace view. */
export interface WorkspaceTickets {
  tenant_id: string;
  workspace_name: string | null;
  open: ManualFixTicket[];
  in_progress: ManualFixTicket[];
  resolved: ManualFixTicket[];
  counts: { open: number; in_progress: number; resolved: number };
}

/** Cross-workspace tickets (GET /technical-audit/tickets/all) — every
 *  manual-fix ticket in every workspace the user is a member of. */
export interface AllTicketsResponse {
  workspaces: WorkspaceTickets[];
  totals: { open: number; in_progress: number; resolved: number };
}

/** One sampled issue in the cross-workspace recommendation backlog. */
export interface RecommendationBacklogIssue {
  id: string;
  issue_type: string;
  severity: string;
  page_url: string | null;
  page_title: string | null;
  generation_status: "queued" | "generating" | "ready" | "failed" | null;
  created_at: string | null;
}

/** One workspace's slice of the recommendation backlog. */
export interface RecommendationBacklogWorkspace {
  tenant_id: string;
  workspace_name: string | null;
  run_id: string;
  total: number;
  by_severity: Record<string, number>;
  by_type: { issue_type: string; count: number }[];
  /** Severity-ordered sample capped server-side (~25); `total` carries scale. */
  issues: RecommendationBacklogIssue[];
}

/** Cross-workspace recommendation backlog
 *  (GET /technical-audit/recommendation-backlog/all): issues from each member
 *  workspace's latest completed crawl that have no AI recommendation yet. */
export interface RecommendationBacklogResponse {
  workspaces: RecommendationBacklogWorkspace[];
  totals: { issues: number; workspaces: number };
}

/** Manual-fix tickets for a tenant, across all crawls (GET …/tickets). */
export interface TicketsResponse {
  tenant_id: string;
  open: ManualFixTicket[];
  in_progress: ManualFixTicket[];
  resolved: ManualFixTicket[];
  counts: { open: number; in_progress: number; resolved: number };
}

// ============================================================================
// Bulk duplicate review (BDD A5) — consolidated duplicate groups
// ============================================================================

/** One consolidated duplicate group: N pages sharing the same title/meta/H1. */
export interface DuplicateGroup {
  issue_type: "duplicate_title" | "duplicate_meta" | "duplicate_h1";
  /** The shared <title> / meta description / H1 that defines the group. */
  shared_value: string | null;
  member_count: number;
  /** Members still actionable (not applied/verified/skipped/etc.). */
  actionable_count: number;
  /** Members that already have a generated suggestion ready for review. */
  with_suggestion_count: number;
  issue_ids: string[];
  actionable_issue_ids: string[];
  /** Fully-decorated issues — same shape as the issues list. */
  issues: PageIssue[];
  /** duplicate_h1 only: the shared heading appears to be rendered by a shared
   *  theme/layout element (per-page applies failed with the builder/theme
   *  hand-off) — fix is ONE theme edit / ONE expert ticket, not N applies. */
  global_template_h1?: boolean;
  global_template_evidence_count?: number;
}

/** GET /technical-audit/runs/{run_id}/duplicate-groups */
export interface DuplicateGroupsResponse {
  run_id: string;
  group_count: number;
  total_issues: number;
  groups: DuplicateGroup[];
}

/** POST /technical-audit/issues/bulk-generate-suggestions */
export interface BulkGenerateSuggestionsResponse {
  run_id: string | null;
  queued: number;
  auto_suggest_queued_issue_ids: string[];
  skipped_terminal: number;
  skipped_already_has_suggestion: number;
  skipped_unsupported_type: number;
  not_found: number;
}

/** Result of publishing an unpublished source page (POST …/publish-source). */
export interface PublishSourceResult {
  success: boolean;
  wp_post_id?: number | null;
  status?: string | null;
  message?: string | null;
  error?: string | null;
}

// ============================================================================
// Issue Details (varies by issue_type)
// ============================================================================

export interface TitleMetaDetails {
  current_title: string | null;
  current_meta_description: string | null;
  title_length: number | null;
  meta_length: number | null;
  title_missing: boolean;
  title_too_long: boolean;
  title_too_short: boolean;
  meta_missing: boolean;
  meta_too_long: boolean;
  meta_too_short: boolean;
  // The atomic title/meta runtime types (page_title_over_60_chars,
  // meta_description_below_70_chars, etc.) reuse this detail shape and add the
  // relevant length bound. Optional so legacy combined `title_meta` rows (which
  // carry the boolean flags above) keep working unchanged.
  max_length?: number | null;
  min_length?: number | null;
}

export interface H1Details {
  /**
   * NOT guaranteed. This was typed as a required `string[]`, which is a promise the
   * backend does not keep: the DFS `h1_missing` detector emits only
   * `{dfs_check: "no_h1_tag"}` with no heading list, and some rows carry the
   * singular `current_h1`. Because the type lied, TypeScript never flagged the
   * unguarded `details.current_h1s[0]` in TextDiffCard, and opening the fix drawer
   * on such an issue white-screened the page (seen live on
   * certifiedconcreteconstruction.com/blog/).
   */
  current_h1s?: string[];
  /** Singular variant emitted by some detectors instead of `current_h1s`. */
  current_h1?: string;
  h1_count?: number;
  h1_missing?: boolean;
  multiple_h1s?: boolean;
  h1_too_long?: boolean;
  h1_1_length?: number;
  max_length?: number | null;
}

export interface AltTextDetails {
  image_url: string;
  image_filename: string | null;
  current_alt_text: string | null;
  image_type: string | null;
}

export interface RedirectDetails {
  redirect_chain: string[];
  final_url: string;
  redirect_count: number;
  redirect_type: string;
}

export interface BrokenLinkDetails {
  status_code: number;
  target_url: string;
  source_url: string;
  link_text: string | null;
}

export interface CanonicalDetails {
  canonical_url?: string;
  canonical_1?: string;
  canonical_2?: string;
  canonical_urls?: string[];
  canonical_count?: number;
  page_url?: string;
  issue_type?: "missing" | "multiple" | "mismatch";
  canonical_missing?: boolean;
  multiple_canonicals?: boolean;
  canonical_mismatch?: boolean;
  recommended_canonical?: string;
  // Optional structured root cause when the backend/ingest provides it
  // (missing / mismatch / multiple / same_domain / cross_domain).
  canonical_cause?: string;
}

export interface DuplicateTitleDetails {
  title: string;
  duplicate_urls: string[];
  count: number;
}

export interface DuplicateMetaDetails {
  description: string;
  duplicate_urls: string[];
  count: number;
}

export interface ThinContentDetails {
  word_count: number;
  page_url: string;
  threshold: number;
}

export interface IndexabilityDetails {
  page_url: string;
  indexability_status: string;
  reason?: string;
  // B1: optional structured root cause when the backend/ingest provides it.
  indexability_cause?: string;
  canonical_url?: string;
}

// noindex_important: a noindexed page the detector already proved important
// (listed in the XML sitemap, internally linked, shallow crawl depth). The
// backend attaches the evidence so the UI can show WHY it matters.
export interface NoindexImportantDetails {
  page_url?: string;
  /** e.g. "noindex" — the crawl's indexability label for the page. */
  indexability_status?: string;
  /** Where the noindex comes from: "meta" | "header" | "robots_txt". Only
   *  page-level meta noindex has an automatic fix path. */
  noindex_source?: string;
  /** Human-readable evidence summary, e.g.
   *  "14 internal inlinks; crawl depth 1; listed in XML sitemap". */
  importance_reason?: string;
  in_sitemap?: boolean;
  crawl_depth?: number;
  unique_inlinks?: number;
}

export interface SlowResponseDetails {
  response_time_ms: number;
  page_url: string;
  threshold_ms: number;
}

export interface NearDuplicateDetails {
  page_url: string;
  similar_url: string;
  similarity_score: number;
}

export interface OrphanPageDetails {
  page_url: string;
  incoming_links: number;
}

export interface SchemaErrorDetails {
  page_url: string;
  schema_type: string;
  errors: string[];
}

export interface MixedContentDetails {
  page_url: string;
  insecure_url: string;
  resource_type: string;
}

export interface RedirectLoopDetails {
  page_url: string;
  redirect_chain: string[];
  loop_detected_at: string;
  // Emitted by the loop detector / review-only suggestion resolver.
  redirect_loop?: string[];
  loop_length?: number;
}

export interface TitleTooShortDetails {
  page_url: string;
  current_title: string;
  title_length: number;
  min_length: number;
}

export interface SerpTitleTruncatedDetails {
  page_url: string;
  current_title: string;
  pixel_width: number;
  max_pixel_width: number;
}

export interface SerpMetaTruncatedDetails {
  page_url: string;
  current_meta: string;
  character_length: number;
  max_length: number;
}

export interface MultipleMetaDetails {
  page_url: string;
  meta_descriptions: string[];
  count: number;
}

export interface DuplicateH1Details {
  page_url: string;
  h1_text: string;
  duplicate_urls: string[];
  count: number;
}

export interface LowReadabilityDetails {
  page_url: string;
  readability_score: number;
  threshold: number;
  grade_level: string;
}

export interface LowTextRatioDetails {
  page_url: string;
  text_ratio: number;
  threshold: number;
  text_bytes: number;
  html_bytes: number;
}

export interface StaleContentDetails {
  page_url: string;
  last_modified: string;
  days_since_update: number;
  threshold_days: number;
}

export interface BrokenExternalDetails {
  page_url: string;
  target_url: string;
  status_code: number;
  link_text: string | null;
}

// One broken (4xx/5xx) link found on the source page. Mirrors the broken_external
// evidence shape (dead target + anchor) but is carried as a list, since a single
// links_to_broken row aggregates every broken INTERNAL link on one page.
export interface BrokenLinkTarget {
  target_url: string;
  status_code: number | null;
  anchor: string;
}

// links_to_broken — one issue per SOURCE page (issue.page_url): "this page links
// to N broken internal pages". Source-page-anchored, mirrors broken_external but
// lists every dead target. High severity (like broken_internal).
export interface LinksToBrokenDetails {
  page_url?: string;
  broken_links: BrokenLinkTarget[];
  broken_count: number;
}

export interface DeepPageDetails {
  page_url: string;
  click_depth: number;
  max_depth: number;
}

export interface LargeImageDetails {
  page_url: string;
  image_url: string;
  file_size_bytes: number;
  max_size_bytes: number;
}

export interface LargeImageProofResult {
  status?: LargeImageResultState | string | null;
  result_status?: LargeImageResultState | string | null;
  issue_type?: string | null;
  method?: string | null;
  image_context?: string | null;
  attachment_id?: string | number | null;
  original_size?: number | null;
  new_size?: number | null;
  bytes_saved?: number | null;
  new_dimensions?: string | null;
  still_over_max?: boolean | null;
  success?: boolean | null;
  skipped?: boolean | null;
  protected?: boolean | null;
  error?: string | null;
  message?: string | null;
  reason?: string | null;
  warning?: string | null;
  wordpress_response?: unknown;
  [key: string]: unknown;
}

export interface NonNextgenImageDetails {
  page_url: string;
  image_url: string;
  current_format: string;
  recommended_format: string;
}

export interface LargePageDetails {
  page_url: string;
  page_size_bytes: number;
  max_size_bytes: number;
}

export interface UnsafeCrossoriginDetails {
  page_url: string;
  target_url: string;
  rel_attributes: string;
}

export interface NofollowInternalDetails {
  page_url: string;
  target_url: string;
  link_text: string | null;
}

export interface LowInternalLinksDetails {
  page_url: string;
  internal_link_count: number;
  threshold: number;
}

export interface MissingSchemaDetails {
  page_intent: string;
  expected_schema_types: string[];
  present_schema_types: string[];
  present_non_generic_schema_types: string[];
  missing_schema_types: string[];
  evidence_source: string;
}

// One FAQ pair surfaced for a FAQPage schema suggestion. The schema_jsonld is the
// source of truth that gets applied; faqs are a human-readable mirror.
export interface MissingSchemaFaq {
  question: string;
  answer: string;
}

// missing_schema auto-fix payload (the proposed/approved value JSON). The card
// renders + edits `schema_jsonld` (the primary value) and gates Apply on
// `apply_disabled`: Article/FAQ get a live-write path (yoast_article_meta /
// yoast_faq_block); entity types (LocalBusiness/Service/Product/Organization)
// get a JSON-LD injection path (jsonld_injection) ONLY when the backend confirms
// the site's plugin supports it — otherwise they are handed off for manual
// implementation (apply_disabled = true, action = "review"). The frontend never
// decides capability itself; it reads apply_disabled/apply_method off this payload.
export interface MissingSchemaFix {
  // Shared payload shape for missing_schema and schema_validation_error (both edit
  // + apply a JSON-LD block via MissingSchemaCard, gated on apply_disabled).
  issue_type?: "missing_schema" | "schema_validation_error";
  page_intent?: string;
  recommended_schema_type?: string;
  // The generated JSON-LD object (object in practice; string-tolerant for
  // payloads that round-tripped through storage as a JSON string). For
  // schema_validation_error this is the CORRECTED block.
  schema_jsonld?: Record<string, unknown> | string;
  faqs?: MissingSchemaFaq[];
  // schema_validation_error only: the validation errors + a per-error explanation
  // of what the corrected JSON-LD changed. Shown prominently above the JSON-LD.
  validation_errors?: string[];
  error_explanations?: { error?: string; fix?: string }[];
  schema_types?: string[];
  source_jsonld_available?: boolean;
  apply_method?:
    | "yoast_article_meta"
    | "yoast_faq_block"
    | "jsonld_injection"
    | "handoff";
  apply_disabled?: boolean;
  apply_disabled_reason?: string;
  capability_gap?: string;
  action?: "apply" | "review";
  recommendation?: string;
  reasoning?: string;
  auto_fixable?: boolean;
}

// schema_validation_error issue_details (crawler-emitted). The card reads these
// leniently to surface the validation errors above the corrected JSON-LD.
export interface SchemaValidationDetails {
  error_count: number;
  warning_count: number;
  schema_types: string[];
  total_types?: number | null;
  error_messages: string[];
  validation_messages: {
    schema_type?: string | null;
    validation_type?: "Error" | "Warning";
    message?: string | null;
  }[];
  evidence_source: string;
}

export interface MissingOgTagsDetails {
  missing: string[];
  required: string[];
  present: string[];
  evidence_source: string;
}

export interface MisspellingDetails {
  page_url: string;
  source: string;
}
export interface RenderBlockingDetails {
  page_url: string;
}
export interface RedirectingLinkTarget {
  target_url: string;
  status_code: number | null;
  anchor: string;
}

// links_to_redirect — one issue per SOURCE page (issue.page_url): "this page
// links to N internal pages that pass through a 3xx redirect". Source-page-
// anchored (mirrors links_to_broken); the fix repoints each link to its final
// destination. `redirecting_links` is the DFS-enriched shape; legacy issues
// (pre-enrichment) may carry only page_url/target_url.
export interface LinksToRedirectDetails {
  page_url: string;
  target_url?: string;
  redirecting_links?: RedirectingLinkTarget[];
  redirect_count?: number;
}
export interface MissingCompressionDetails {
  page_url: string;
}
export interface BadUrlStructureDetails {
  page_url: string;
}
export interface HasIframeDetails {
  page_url: string;
}

// New recommendation-only Technical Audit issue types (review-only — never
// auto-applied). Backend emits these; the UI surfaces them as recommendations.
export interface KeywordCannibalizationDetails {
  keyword_signature: string;
  competing_count: number;
  competing_urls: string[];
  competing_titles: string[];
  recommendation: string;
  // Optional backend enrichment (frontend computes a fallback when absent):
  // the suggested canonical winner and whether the group looks like geo
  // (location) pages, for which a canonical merge would be harmful.
  suggested_winner?: string;
  is_geo_pattern?: boolean;
}

// Apply payload for a keyword_cannibalization fix: the backend canonical-write
// (_apply_keyword_cannibalization_fix) reads winner_url and points the losing
// page's canonical at it.
export interface KeywordCannibalizationFix {
  winner_url: string;
}

export interface ImageMissingDimensionsDetails {
  page_url: string;
  source_page_url: string;
  image_url: string;
  missing_attributes: string[];
  intrinsic_dimensions: string | null;
  recommendation: string;
}

export interface CoreWebVitalsDetails {
  page_url: string;
  metric: string;
  value: number;
  threshold: number;
  recommendation: string;
}

// B2: sitemap_error details carry a `reason` set at ingest plus the crawl status.
export interface SitemapErrorDetails {
  page_url?: string;
  url?: string;
  status_code?: number;
  reason?: string;
  indexability?: string;
  sitemap_cause?: string;
}

// missing_from_sitemap: a crawled, indexable page absent from the XML sitemap.
// `sitemap_url_count` is how many URLs the sitemap DID list (evidence that the
// sitemap was fetched and parsed, not just missing).
export interface MissingFromSitemapDetails {
  page_url?: string;
  in_sitemap?: boolean;
  sitemap_url_count?: number;
  reason?: string;
  // The detector emits page_title / unique_inlinks (see
  // issue_detector.detect_missing_from_sitemap_issues). title / inlinks are
  // kept as tolerated aliases so a row from any other ingest path still renders.
  page_title?: string;
  unique_inlinks?: number;
  title?: string;
  inlinks?: number;
  word_count?: number;
}

// Why a hreflang_error row was raised. Decides auto-fixability (Phase 1):
// "invalid_lang" / "broken_target" have a deterministic WordPress write;
// "non_reciprocal" (return links on OTHER pages/sites) and "missing_x_default"
// (needs the site's default-language URL chosen) need cross-page/site setup and
// stay recommendation-only / manual until Phase 2. A row can carry MORE than one
// reason: if it has at least one Phase-1 code it is still auto-fixable, and the
// preview's outstanding_reason_codes/outstanding_note describe the Phase-2
// remainder Apply does NOT fix.
export type HreflangReasonCode =
  | "invalid_lang"
  | "broken_target"
  | "non_reciprocal"
  | "missing_x_default"
  | string;

// hreflang_error crawl evidence. `reason` is the human-readable string; new
// rows also carry the machine `reason_code` (legacy rows lack it — the preview
// endpoint's is_auto_fixable stays authoritative either way).
export interface HreflangErrorDetails {
  page_url?: string;
  /** The alternate URL the flagged hreflang tag points at. */
  hreflang_url?: string;
  /** The tag's language/region code as crawled, e.g. "en-uk". */
  hreflang_language?: string;
  reason?: string;
  reason_code?: HreflangReasonCode;
  target_status?: number | string;
  status_code?: number;
}

export type IssueDetails =
  | SitemapErrorDetails
  | TitleMetaDetails
  | H1Details
  | AltTextDetails
  | RedirectDetails
  | BrokenLinkDetails
  | CanonicalDetails
  | DuplicateTitleDetails
  | DuplicateMetaDetails
  | ThinContentDetails
  | IndexabilityDetails
  | NoindexImportantDetails
  | SlowResponseDetails
  | NearDuplicateDetails
  | OrphanPageDetails
  | SchemaErrorDetails
  | MissingSchemaDetails
  | MissingOgTagsDetails
  | MixedContentDetails
  | RedirectLoopDetails
  | TitleTooShortDetails
  | SerpTitleTruncatedDetails
  | SerpMetaTruncatedDetails
  | MultipleMetaDetails
  | DuplicateH1Details
  | LowReadabilityDetails
  | LowTextRatioDetails
  | StaleContentDetails
  | BrokenExternalDetails
  | LinksToBrokenDetails
  | DeepPageDetails
  | LargeImageDetails
  | NonNextgenImageDetails
  | LargePageDetails
  | UnsafeCrossoriginDetails
  | NofollowInternalDetails
  | LowInternalLinksDetails
  | MisspellingDetails
  | RenderBlockingDetails
  | LinksToRedirectDetails
  | MissingCompressionDetails
  | BadUrlStructureDetails
  | HasIframeDetails
  | KeywordCannibalizationDetails
  | ImageMissingDimensionsDetails
  | CoreWebVitalsDetails
  | HreflangErrorDetails;

// ============================================================================
// Fix Proposals (AI-generated)
// ============================================================================

export interface TitleMetaFix {
  // Canonical SEO/meta title-tag key (Yoast/RankMath). `new_title` is a
  // backwards-compatible alias kept for suggestions stored before the split.
  new_seo_title?: string;
  new_title?: string;
  new_meta_description?: string;
  // Which field the suggestion targets. Title/meta fixes are always
  // "seo_title" — they never touch the visible page title or H1.
  target_field?: "seo_title" | "page_title" | "h1";
}

export interface H1PromoteChoice {
  /** Visible text of the heading already on the page. */
  text: string;
  /** Its current level, e.g. "h2" — informational; the apply re-resolves it. */
  level?: string;
  /** Elementor widget id when known; matched before text (stable across edits). */
  element_id?: string;
}

export interface H1Fix {
  // Optional: a promote_existing value (below) carries no new_h1 at all.
  new_h1?: string;
  // multiple-H1 demote: the operator-picked heading that SURVIVES as the H1
  // (the others are demoted to H2). Only meaningful for h1_multiple; the
  // backend ignores new_h1 on that path and honors keep_h1.
  keep_h1?: string;
  reasoning?: string;
  /**
   * The operator chose an EXISTING heading instead of generated wording. Apply
   * promotes that element in place (h2 -> h1) rather than inserting a second
   * heading beside it, which is what produced duplicate headings previously.
   */
  promote_existing?: H1PromoteChoice;
}

export interface H1PromoteCandidate {
  text: string;
  level: string;
  element_id?: string;
  /** "builder" = found in editable content (promotable); "rendered" = live page only. */
  source?: "builder" | "rendered";
}

export interface H1PromoteCandidates {
  issue_id: string;
  candidates: H1PromoteCandidate[];
  builder: string | null;
  /** False when the page's headings cannot be promoted automatically. */
  promotable: boolean;
  reason: string | null;
}

export interface AltTextFix {
  new_alt_text: string;
  // When set, Apply replaces the image in WordPress with this candidate (gated).
  replacement_image_url?: string;
}

export interface RedirectFix {
  recommended_target: string;
  reasoning?: string;
}

export interface BrokenLinkFix {
  replacement_url?: string;
  action: "remove" | "replace" | "update";
  reasoning?: string;
}

// Emitted by the backend technical_audit_canonical_resolver generator + apply.
// The suggestion payload is stored as JSON in proposed_value/approved_value:
//   { action: "set_canonical", recommended_canonical, canonical_cause, review_only:false, reasoning }
//   { action: "review",        canonical_cause: "cross_domain", review_only:true, reasoning }
// _apply_canonical_fix surfaces canonical_cause/canonical_url/method/before_state
// in the apply result envelope (success) or review_only:true on the cross-domain gate.
export interface CanonicalFix {
  recommended_canonical?: string;
  reasoning?: string;
  // "set_canonical" => safe self-canonical write; "review" => cross-domain gate.
  action?: "set_canonical" | "review" | string;
  // Why the canonical was flagged: missing / mismatch / multiple / same_domain /
  // cross_domain (the last is the review-only gate).
  canonical_cause?: string;
  // True when the resolver/apply declined an automatic write (cross-domain).
  review_only?: boolean;
  // The canonical actually written on a successful apply.
  canonical_url?: string;
  method?: string;
  generation_source?: "technical_audit_canonical_resolver" | string;
}

// Emitted by the backend technical_audit_broken_external_resolver generator.
// Stored as JSON in proposed_value / surfaced as the suggestion. "remove" keeps
// the anchor text and strips the dead link; supplying a replacement_url switches
// the recommended action to "repoint".
export interface BrokenExternalFix {
  action: "remove" | "repoint";
  broken_target_url?: string;
  source_url?: string;
  link_text?: string | null;
  replacement_url?: string | null;
  status_code?: number;
  verified_status_code?: number;
  confidence?: string;
  reasoning?: string;
  generation_source?: "technical_audit_broken_external_resolver" | string;
}

// One resolved broken INTERNAL link on the source page: repoint it to a live
// replacement, or remove it (keeping its anchor text). Mirrors the per-target
// broken_external decision, batched.
export interface LinksToBrokenEdit {
  target_url: string;
  anchor: string;
  status_code: number | null;
  confidence?: string;
  match_score?: number;
  action: "repoint" | "remove";
  replacement_url: string | null;
}

// Emitted by the backend technical_audit_links_to_broken_resolver generator and
// stored in proposed_value / approved_value. The generic Apply flow posts the
// approved value back; the backend reads `.edits`.
export interface LinksToBrokenFix {
  action: "repoint_or_remove_broken_links";
  source_page_url: string;
  edits: LinksToBrokenEdit[];
  broken_count: number;
  repoint_count: number;
  remove_count: number;
  generation_source?: "technical_audit_links_to_broken_resolver" | string;
}

export interface DuplicateTitleFix {
  // Canonical SEO/meta title-tag key (Yoast/RankMath). `new_title` is the
  // backwards-compatible alias for suggestions stored before the split.
  new_seo_title?: string;
  new_title?: string;
  target_field?: "seo_title" | "page_title" | "h1";
  reasoning?: string;
}

export interface DuplicateMetaFix {
  new_meta_description: string;
  reasoning?: string;
}

export interface ThinContentExpansionSection {
  heading: string;
  purpose?: string;
  draft?: string;
  /**
   * Per-section review state, mirroring low_readability's per-sentence accept.
   * CLIENT-SIDE ONLY: the card filters on it and strips it before emitting, so
   * it never reaches the backend, which composes whatever sections it is handed.
   *
   * `undefined` means "not touched" and counts as accepted — a brief generated
   * before per-section review existed still applies whole. Only an explicit
   * `false` removes a section from the write.
   */
  accepted?: boolean;
}

// Blog-vs-page classification for a thin_content page. Drives the card's badge
// and the CRO-risk banner. `source` records how the classification was derived;
// "default" means the backend could not confirm the page type and treated it as
// a conversion page (CRO-risk) as a precaution.
export interface ThinContentClassification {
  type: "blog" | "page";
  expansion_friendly: boolean;
  cro_risk: boolean;
  source: "internal_pages_index" | "url_pattern" | "default";
}

export interface ThinContentFix {
  action: "expand" | "merge" | "noindex" | "review";
  recommendation?: string;
  summary?: string;
  recommended_sections?: ThinContentExpansionSection[];
  draft_additions?: string[];
  evidence_needed?: string[];
  internal_link_opportunities?: string[];
  target_additional_words?: number;
  manual_application_required?: boolean;
  apply_disabled?: boolean;
  apply_disabled_reason?: string;
  reasoning?: string;
  // Packet copy of the blog-vs-page classification (the card prefers the
  // top-level PageIssue.thin_content_classification when present).
  thin_content_classification?: ThinContentClassification;
  // Phase 1 (blog-only additive apply): false when the generator could not read
  // the live post body, so the brief isn't grounded in live content. When false,
  // the card shows a caution banner and the blog apply control is disabled (we
  // won't append to a body we couldn't read). Field name pending backend #5/#6
  // confirmation; treated as optional (absent => treat as grounded).
  grounded?: boolean;
}

export interface IndexabilityFix {
  recommended_action: string;
  reasoning?: string;
  indexability_cause?: string;
}

export interface SlowResponseFix {
  recommended_action: string;
  reasoning?: string;
}

export interface GenericFix {
  recommendation: string;
  auto_fixable: boolean;
  reasoning?: string;
}

export type NearDuplicateFix = GenericFix;

// A single selected source blog the inbound internal link is spread across.
// One link is inserted per selected blog (capped at 5 by the backend apply).
export interface InternalLinkAnchorCandidate {
  id?: string;
  candidate_id?: string;
  anchor_text?: string;
  suggested_anchor_text?: string;
  snippet?: string | null;
  insertion_mode?: "sentence_rewrite" | string;
  original_sentence?: string;
  source_sentence?: string;
  rewritten_sentence?: string;
  score?: number | Record<string, unknown> | null;
  candidate_score?: number | Record<string, unknown> | null;
  fit_score?: number | null;
  confidence?: number | null;
  reason?: string;
  matched_terms?: string[];
  verified?: boolean;
  has_anchor?: boolean;
  source?: string;
}

export interface OrphanPageSourcePage {
  source_page_url: string;
  anchor_text: string;
  title?: string;
  snippet?: string | null;
  insertion_mode?: "sentence_rewrite" | string;
  original_sentence?: string;
  source_sentence?: string;
  rewritten_sentence?: string;
  post_type?: string;
  match_score?: number;
  has_anchor?: boolean;
  manual_review_required?: boolean;
  needs_anchor_selection?: boolean;
  source?: string;
  borderline?: boolean;
  fit_score?: number | null;
  confidence?: number | null;
  suggested_anchor_text?: string;
  anchor_selection_status?: "verified" | "needs_anchor_selection" | string;
  anchor_candidates?: InternalLinkAnchorCandidate[];
  placement_candidates?: InternalLinkAnchorCandidate[];
  selected_anchor_candidate?: InternalLinkAnchorCandidate | null;
}

export interface OrphanPageFix {
  action?: string;
  source_page_url?: string;
  anchor_text?: string;
  recommendation?: string;
  reasoning?: string;
  target_link_strategy?: string;
  selection_outcome?: string;
  orphan_page_url?: string;
  suggested_internal_link_plan?: string[];
  // The selected spread of source blogs (one inbound link per blog). The
  // backend default pre-selects the top 3; the card lets the specialist
  // add/remove blogs from `recommended_source_pages`.
  source_pages?: OrphanPageSourcePage[];
  // How many source blogs are currently selected for the spread.
  selected_count?: number;
  recommended_source_pages?: Array<{
    url: string;
    title: string;
    suggested_anchor_text?: string | null;
    match_score?: number;
    // Sources are blog posts only now; these describe where the link lands.
    post_type?: string;
    // The section/sentence in the source blog where the link will land (with
    // the anchor phrase inside), or null when it will be appended at the end.
    snippet?: string | null;
    // True when the anchor phrase already exists in the source body.
    has_anchor?: boolean;
    manual_review_required?: boolean;
    needs_anchor_selection?: boolean;
    source?: string;
    borderline?: boolean;
    fit_score?: number | null;
    confidence?: number | null;
    llm_selected?: boolean;
    llm_candidate_id?: string;
    llm_confidence?: number | null;
    llm_reasoning?: string;
    anchor_selection_status?: "verified" | "needs_anchor_selection" | string;
    anchor_candidates?: InternalLinkAnchorCandidate[];
    placement_candidates?: InternalLinkAnchorCandidate[];
    selected_anchor_candidate?: InternalLinkAnchorCandidate | null;
  }>;
}
export type SchemaErrorFix = GenericFix;
export type MixedContentFix = GenericFix;
export type RedirectLoopFix = GenericFix;
export type TitleTooShortFix = GenericFix;
export type SerpTitleTruncatedFix = GenericFix;
export type SerpMetaTruncatedFix = GenericFix;
export type MultipleMetaFix = GenericFix;
export type DuplicateH1Fix = GenericFix;

// One sentence-level readability edit. Carries its location so the backend can
// reassemble approved edits against the live body with drift checks.
/** Why a sentence scored as hard — the arithmetic behind the grade, not a label. */
export interface ReadabilityReason {
  code: "long_sentence" | "very_long_sentence" | "complex_words" | string;
  label: string;
  detail: string;
  value?: number;
}

export interface SentenceDiff {
  block_index: number;
  sentence_index: number;
  original: string;
  proposed: string;
  grade_before?: number | null;
  grade_after?: number | null;
  reasons?: ReadabilityReason[];
}

export interface LowReadabilityFix {
  mode?: "block" | "sentence";
  rewritten_body?: string;
  original_body?: string;
  sentence_diffs?: SentenceDiff[];
  readability_score_before?: number;
  readability_score_after?: number;
  grade_before?: string;
  grade_after?: string;
  blocks_rewritten?: number;
  blocks_total?: number;
  sentences_rewritten?: number;
  sentences_eligible?: number;
  reasoning?: string;
  improved?: boolean;
}
export type LowTextRatioFix = GenericFix;
export type StaleContentFix = GenericFix;
export type BrokenExternalFix = GenericFix;
export type DeepPageFix = GenericFix;
export type LargeImageFix = GenericFix;
export type NonNextgenImageFix = GenericFix;
export type LargePageFix = GenericFix;
export type UnsafeCrossoriginFix = GenericFix;
export type NofollowInternalFix = GenericFix;
// low_internal_links shares the orphan_page internal-link fix shape: pick a
// source page + editable anchor text, then add a contextual link to the
// under-linked target page.
export type LowInternalLinksFix = OrphanPageFix;
export type MisspellingFix = GenericFix;
export type RenderBlockingFix = GenericFix;
export type LinksToRedirectFix = GenericFix;
export type MissingCompressionFix = GenericFix;
export type BadUrlStructureFix = GenericFix;
export type HasIframeFix = GenericFix;

// A2: AI confidence / relevance score attached to every generated fix.
export interface ConfidenceMeta {
  confidence_score?: number; // 0-100
  confidence_label?: "high" | "medium" | "low" | "none";
  confidence_reasoning?: string | null; // why the judge scored it this way
  confidence_source?: string | null; // e.g. "judge:google/gemini-3-flash-preview"
}

// The reviewed value applied for a multiple_meta consolidation. The single chosen
// meta description is written to BOTH the post excerpt AND the SEO-plugin meta so
// every emitted <meta name="description"> carries the same value. MultipleMetaCard
// lifts this from the preview endpoint (planned_payload) so Apply writes exactly
// the description shown inline. `new_meta_description` is a backend-accepted alias.
export interface MultipleMetaApplyFix {
  recommended_meta_description: string;
  new_meta_description?: string;
  recommended_source?: MultipleMetaRecommendedSource;
  recommendation_reason?: string | null;
  reasoning?: string;
}

export type ProposedFix = (
  | TitleMetaFix
  | H1Fix
  | AltTextFix
  | RedirectFix
  | BrokenLinkFix
  | BrokenExternalFix
  | LinksToBrokenFix
  | CanonicalFix
  | DuplicateTitleFix
  | DuplicateMetaFix
  | ThinContentFix
  | IndexabilityFix
  | SlowResponseFix
  | OrphanPageFix
  | LowInternalLinksFix
  | LowReadabilityFix
  | KeywordCannibalizationFix
  | MultipleMetaApplyFix
  | MissingSchemaFix
  | GenericFix
) &
  ConfidenceMeta;

// ============================================================================
// Main Issue Type
// ============================================================================

export interface PageIssue {
  id: string;
  run_id: string;
  issue_type: IssueType;
  source?: "screaming_frog" | "dataforseo" | string;
  severity: IssueSeverity;
  page_url: string;
  page_title: string | null;
  status_code: number | null;
  indexability: string | null;
  issue_details: IssueDetails;
  fix_status: IssueStatus;
  original_value?: string | null;
  proposed_value?: string | null;
  proposed_fix: ProposedFix | null;
  approved_value: ProposedFix | null; // User-edited value
  error: string | null;
  created_at: string;
  approved_at?: string | null;
  applied_at?: string | null;
  large_image_result?: LargeImageProofResult | null;
  apply_result?: LargeImageProofResult | Record<string, unknown> | null;
  fix_result?: LargeImageProofResult | Record<string, unknown> | null;
  result?: LargeImageProofResult | Record<string, unknown> | null;
  content_type?: "page" | "post" | "product" | "media" | null;
  wordpress_id?: number | null;
  modified_date?: string | null;
  // Backend blog-vs-page auto-fix gate (derived server-side, not stored).
  // "page" => excluded from auto-fix (only blog posts are auto-fixed).
  fix_target?: "blog" | "page" | null;
  auto_fix_excluded_reason?: string | null;
  // Background auto-suggestion generation state (MVP). Null when the row was
  // never auto-queued. Surfaced as a top-level field by the list response.
  generation_status?: GenerationStatus | null;
  // Blog-vs-page classification for thin_content issues. Emitted top-level by
  // the backend during serialization; the card prefers this over the packet
  // copy on the fix. Absent for non-thin_content issues.
  thin_content_classification?: ThinContentClassification;
}

// ============================================================================
// Fix history / audit log + rollback (fathom §3 #4/#6)
// ============================================================================

export interface FixHistoryEntry {
  id: string;
  action:
    | "approve"
    | "reject"
    | "apply"
    | "revert"
    | "rollback"
    | "verify"
    | string;
  success?: boolean;
  applied_by?: string | null;
  applied_at?: string | null;
  method?: string | null;
  error?: string | null;
  summary?: string;
  reverted_at?: string | null;
  reverted_by?: string | null;
  revertible?: boolean;
  issue_id?: string;
  run_id?: string | null;
  tenant_id?: string | null;
  actor_email?: string | null;
  old_status?: string | null;
  new_status?: string | null;
  diff?: Record<string, unknown> | null;
  snapshot?: Record<string, unknown> | null;
  created_at?: string | null;
}

export interface FixHistory {
  issue_id: string;
  fix_status: IssueStatus | string;
  can_revert: boolean;
  entries: FixHistoryEntry[];
}

// ============================================================================
// API Request/Response Types
// ============================================================================

export interface IssueFilters {
  type?: IssueType;
  severity?: IssueSeverity;
  status?: IssueStatus;
  search?: string;
}

export interface IssueSort {
  field: "page_url" | "issue_type" | "severity" | "status" | "created_at";
  direction: "asc" | "desc";
}

export interface IssuesListResponse {
  issues: PageIssue[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
  summary: IssuesSummary;
}

export interface IssuesSummary {
  total: number;
  by_type: Record<IssueType, number>;
  by_severity: Record<IssueSeverity, number>;
  by_status: Record<IssueStatus, number>;
}

export interface UpdateIssueRequest {
  fixStatus: IssueStatus;
  // Legacy backend field name. UI copy should call this a suggested/reviewed value.
  approvedValue?: string | ProposedFix;
  rejectionReason?: string;
}

export interface BulkUpdateRequest {
  issueIds: string[];
  // Legacy approve/reject remain backend-compatible. The primary UI sends skip
  // when the user chooses not to apply a suggested fix.
  action: "approve" | "reject" | "skip";
  rejectionReason?: string;
}

export interface BulkUpdateResponse {
  updatedCount: number;
  failedCount: number;
  errors: string[];
  message: string;
  hiddenInternalLinkApprovalCount?: number;
  terminalStatusSkipCount?: number;
  missingGeneratedFixApprovalCount?: number;
}

export interface ApplyFixesRequest {
  run_id: string;
}

export interface ApplyFixesResponse {
  applied_count: number;
  failed_count: number;
  skipped_count?: number;
  hidden_internal_link_apply_count?: number;
  // excluded_reason (e.g. thin_content "page_builder" | "page") is carried per
  // error in bulk apply; the reviewed-expansion apply uses the single-issue path
  // (where excluded_reason is top-level), so no bulk UI consumes this today.
  errors: Array<{
    issue_id: string;
    page_url: string;
    error: string;
    excluded_reason?: string;
  }>;
}

// Phase 1 thin_content blog-only additive apply result (append-only WP write).
// Verified against backend _apply_thin_content_fix (#5): returned NESTED under
// ApplySingleFixResponse.result (NOT flat). On failure the envelope drops result
// and only surfaces success/fix_status/error.
export interface ThinContentApplyResult {
  success?: boolean;
  issue_type?: string; // "thin_content"
  method?: string; // exact literal "wp-content-append"
  page_url?: string;
  endpoint?: string;
  post_id?: number;
  added_sections?: number; // COUNT of sections with non-empty draft
  words_added?: number; // plain-text word count of appended HTML
  before_state?: {
    kind?: string;
    endpoint?: string;
    post_id?: number;
    body?: string;
    revertible?: boolean;
  };
}

export interface ApplySingleFixResponse {
  success: boolean;
  fix_status?: IssueStatus | LargeImageResultState | string;
  error?: string;
  message?: string;
  // Top-level no-op / refusal flags returned by the backend on `success: false`.
  // `requires_approval` (large-image approval gate), `skipped` + `excluded_reason`
  // (page-excluded auto-fix), and `fix_target` are all FLAT on the response, not
  // nested under `result`. Surfaced so the UI can show the specific reason.
  requires_approval?: boolean;
  apply_hidden?: boolean;
  skipped?: boolean;
  // Review-only safeguard decline (intentional noindex/canonical) and the
  // "Apply anyway" override outcome when no safe automatic write exists. Both
  // leave the issue pending and are surfaced as info, not "Apply failed".
  review_only?: boolean;
  manual_required?: boolean;
  // Top-level on failure/no-op. For thin_content blog apply failures the backend
  // surfaces "page_builder" (body is page-builder-managed, no editable HTML) or
  // "page" (defense-in-depth non-blog refusal); kept as string since other issue
  // types use their own values.
  excluded_reason?: string;
  fix_target?: "blog" | "page" | string | null;
  issue_type?: string;
  // The fix_result envelope. For thin_content blog apply this is a
  // ThinContentApplyResult (method/added_sections/words_added/before_state).
  result?:
    | (LargeImageProofResult & {
        requires_manual_application?: boolean;
        rendered?: boolean;
      })
    | ThinContentApplyResult
    | null;
}

export interface TechnicalAuditFixPreview {
  issue_id: string;
  issue_type: IssueType;
  is_auto_fixable: boolean;
  fix_family: string;
  tool_family: string | null;
  verification_mode: string;
  manual_reason: string | null;
  // Why an issue that is auto-fixable in general is NOT auto-fixable for this
  // row (e.g. a non_reciprocal hreflang error needs the whole language
  // cluster — Phase 2). Emitted alongside is_auto_fixable:false.
  auto_fix_excluded_reason?: string | null;
  planned_payload: Record<string, unknown>;
}

// Which of the existing meta-description tags the LLM picked as the canonical
// one. "synthesized" means it wrote a fresh single description instead of
// keeping either existing tag verbatim.
export type MultipleMetaRecommendedSource =
  | "meta_1"
  | "meta_2"
  | "synthesized"
  | string;

// The `planned_payload` shape the preview endpoint returns for an
// `image_missing_dimensions` issue when the dry-run resolved a content edit
// (`apply_preview_status: "ready"`). `width`/`height` are the measured values
// the apply would write; `before_tag`/`after_tag` are the exact <img> strings.
export interface ImageDimensionsPreviewPayload {
  ready: boolean;
  width?: number;
  height?: number;
  before_tag?: string;
  after_tag?: string;
  dimension_source?: string;
}

// Narrow an open `planned_payload` to the image-dimensions preview fields.
// `ready` is true only when the backend dry-run produced an applyable edit
// (status "ready" + both measured dimensions present).
export const readImageDimensionsPreview = (
  payload: Record<string, unknown> | null | undefined,
): ImageDimensionsPreviewPayload => {
  const p = (payload ?? {}) as Record<string, unknown>;
  const asPositiveInt = (v: unknown): number | undefined =>
    typeof v === "number" && Number.isInteger(v) && v > 0 ? v : undefined;
  const asStr = (v: unknown): string | undefined =>
    typeof v === "string" && v.trim().length > 0 ? v : undefined;
  const width = asPositiveInt(p.width);
  const height = asPositiveInt(p.height);
  return {
    ready:
      p.apply_preview_status === "ready" &&
      width !== undefined &&
      height !== undefined,
    width,
    height,
    before_tag: asStr(p.before_tag),
    after_tag: asStr(p.after_tag),
    dimension_source: asStr(p.dimension_source),
  };
};

/** Manual width/height override for an image_missing_dimensions apply. Sent as
 *  top-level `{width, height}` in the apply POST body — the backend then skips
 *  measurement, writes exactly these values (subject to its inline-style /
 *  single-attr correctness adjustments), and reports
 *  `dimension_source: "manual_override"`. Both fields required, 1..8192. */
export interface ImageDimensionsOverride {
  width: number;
  height: number;
}

// The `planned_payload` shape the preview endpoint returns for a
// `multiple_meta` issue. `recommendation`,
// `current_meta_descriptions`, and `meta_description_count` are always present;
// the `recommended_*` fields appear only when the LLM single-meta suggestion
// succeeded.
export interface MultipleMetaPreviewPayload {
  recommendation?: string;
  current_meta_descriptions?: string[];
  meta_description_count?: number;
  recommended_meta_description?: string;
  recommended_source?: MultipleMetaRecommendedSource;
  recommendation_reason?: string | null;
}

// Narrow an open `planned_payload` to the multiple-meta preview fields. Returns
// the recommended single meta description (and which existing tag it maps to)
// only when the backend LLM suggestion succeeded.
export const readMultipleMetaPreview = (
  payload: Record<string, unknown> | null | undefined,
): MultipleMetaPreviewPayload => {
  const p = (payload ?? {}) as Record<string, unknown>;
  const asStr = (v: unknown): string | undefined =>
    typeof v === "string" && v.trim().length > 0 ? v : undefined;
  const descriptions = Array.isArray(p.current_meta_descriptions)
    ? (p.current_meta_descriptions.filter(
        (d): d is string => typeof d === "string",
      ) as string[])
    : undefined;
  return {
    recommendation: asStr(p.recommendation),
    current_meta_descriptions: descriptions,
    meta_description_count:
      typeof p.meta_description_count === "number"
        ? p.meta_description_count
        : descriptions?.length,
    recommended_meta_description: asStr(p.recommended_meta_description),
    recommended_source: asStr(p.recommended_source),
    recommendation_reason: asStr(p.recommendation_reason) ?? null,
  };
};

// One rendered hreflang alternate: <link rel="alternate" hreflang="x" href="y">.
export interface HreflangEntry {
  hreflang: string;
  href: string;
}

// One planned edit to the page's hreflang tag set.
//   normalize_code: invalid code corrected in place (en-UK -> en-GB)
//   repoint:        broken target replaced with a live URL
//   remove:         entry dropped (to = null)
export type HreflangChangeType = "normalize_code" | "repoint" | "remove" | string;

export interface HreflangChange {
  type: HreflangChangeType;
  from: HreflangEntry;
  to: HreflangEntry | null;
}

// One page of a CLUSTER (two-page) hreflang fix: the issue page A and its
// partner page B, each with the exact before/after tag set Apply writes.
// Order in `pages` is fixed by the contract: [issue page A, partner page B].
export interface HreflangClusterPage {
  url: string;
  post_id: number;
  before_entries: HreflangEntry[];
  after_entries: HreflangEntry[];
}

// Backend triage decision for a cluster fix.
export type HreflangEvidenceDecision =
  | "auto_apply"
  | "needs_human"
  | "refused"
  | string;

// Per-URL language detection evidence backing the language_verified gate.
export interface HreflangDetectedLanguage {
  url: string;
  language: string;
  // 0..1 (or 0..100 from older payloads — display code normalizes).
  confidence: number;
  words: number;
}

// Why a cluster fix was safe to auto-apply (or why it was refused). `gates`
// holds the individual machine-checkable safety gates (true = passed), e.g.
// partner_same_install / canonical_ok / noindex_ok / language_verified.
// `refusal_reason` is the human copy shown when the row is NOT auto-applicable.
export interface HreflangClusterEvidence {
  decision: HreflangEvidenceDecision;
  gates: Record<string, boolean>;
  detected_languages?: HreflangDetectedLanguage[];
  refusal_reason?: string;
}

// The `planned_payload` shape the preview endpoint returns for an auto-fixable
// `hreflang_error` issue (fix_kind "hreflang"). mode "replace" rewrites the tag
// set to after_entries; mode "suppress" removes ALL hreflang tags from the page
// (bogus hreflang on a single-language site) — after_entries is [].
export interface HreflangPreviewPayload {
  fix_kind: "hreflang";
  mode: "replace" | "suppress" | string;
  reason_code?: HreflangReasonCode;
  before_entries: HreflangEntry[];
  after_entries: HreflangEntry[];
  changes: HreflangChange[];
  // Multi-reason + partial-apply (optional for back-compat with rows that
  // predate them). `reason_codes` lists EVERY resolved reason on the row;
  // `outstanding_reason_codes` is the Phase-2 remainder Apply does NOT fix (may
  // be []); `outstanding_note` explains, in prose, what stays manual.
  reason_codes?: HreflangReasonCode[];
  outstanding_reason_codes?: HreflangReasonCode[];
  outstanding_note?: string | null;
  // CLUSTER (two-page) fix: `cluster: true` means Apply writes hreflang tags to
  // BOTH pages in `pages` atomically ([issue page A, partner page B]).
  // `evidence` explains why that was safe (or why it was refused). All three
  // are optional — single-page payloads simply omit them.
  cluster?: boolean;
  pages?: HreflangClusterPage[];
  evidence?: HreflangClusterEvidence;
}

const asHreflangEntry = (value: unknown): HreflangEntry | null => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const v = value as Record<string, unknown>;
  if (typeof v.hreflang !== "string" || typeof v.href !== "string") return null;
  return { hreflang: v.hreflang, href: v.href };
};

// Narrow an open `planned_payload` to the staged hreflang plan. Returns null
// unless the payload is actually a hreflang plan (fix_kind === "hreflang"), so
// callers can fall back to the advisory rendering for recommendation-only rows
// (non_reciprocal) and legacy payload shapes.
export const readHreflangPreview = (
  payload: Record<string, unknown> | null | undefined,
): HreflangPreviewPayload | null => {
  const p = (payload ?? {}) as Record<string, unknown>;
  if (p.fix_kind !== "hreflang") return null;
  const entries = (value: unknown): HreflangEntry[] =>
    Array.isArray(value)
      ? value
          .map(asHreflangEntry)
          .filter((e): e is HreflangEntry => e !== null)
      : [];
  const changes: HreflangChange[] = Array.isArray(p.changes)
    ? (p.changes as unknown[]).flatMap((raw): HreflangChange[] => {
        if (!raw || typeof raw !== "object" || Array.isArray(raw)) return [];
        const c = raw as Record<string, unknown>;
        const from = asHreflangEntry(c.from);
        if (typeof c.type !== "string" || !from) return [];
        return [{ type: c.type, from, to: asHreflangEntry(c.to) }];
      })
    : [];
  const codeList = (value: unknown): HreflangReasonCode[] | undefined =>
    Array.isArray(value)
      ? (value.filter(
          (c): c is string => typeof c === "string" && c.length > 0,
        ) as HreflangReasonCode[])
      : undefined;
  // Cluster (two-page) fields — pass through only well-formed pages/evidence.
  const pages: HreflangClusterPage[] | undefined = Array.isArray(p.pages)
    ? (p.pages as unknown[]).flatMap((raw): HreflangClusterPage[] => {
        if (!raw || typeof raw !== "object" || Array.isArray(raw)) return [];
        const pg = raw as Record<string, unknown>;
        if (typeof pg.url !== "string" || !pg.url) return [];
        return [
          {
            url: pg.url,
            post_id: typeof pg.post_id === "number" ? pg.post_id : 0,
            before_entries: entries(pg.before_entries),
            after_entries: entries(pg.after_entries),
          },
        ];
      })
    : undefined;
  const evidence: HreflangClusterEvidence | undefined = (() => {
    const ev = p.evidence;
    if (!ev || typeof ev !== "object" || Array.isArray(ev)) return undefined;
    const e = ev as Record<string, unknown>;
    const gates: Record<string, boolean> = {};
    if (e.gates && typeof e.gates === "object" && !Array.isArray(e.gates)) {
      for (const [key, val] of Object.entries(
        e.gates as Record<string, unknown>,
      )) {
        if (typeof val === "boolean") gates[key] = val;
      }
    }
    const detected = Array.isArray(e.detected_languages)
      ? (e.detected_languages as unknown[]).flatMap(
          (raw): HreflangDetectedLanguage[] => {
            if (!raw || typeof raw !== "object" || Array.isArray(raw)) return [];
            const d = raw as Record<string, unknown>;
            if (typeof d.url !== "string" || typeof d.language !== "string")
              return [];
            return [
              {
                url: d.url,
                language: d.language,
                confidence:
                  typeof d.confidence === "number" ? d.confidence : 0,
                words: typeof d.words === "number" ? d.words : 0,
              },
            ];
          },
        )
      : undefined;
    return {
      decision: typeof e.decision === "string" ? e.decision : "needs_human",
      gates,
      detected_languages: detected,
      refusal_reason:
        typeof e.refusal_reason === "string" && e.refusal_reason.trim()
          ? e.refusal_reason
          : undefined,
    };
  })();
  return {
    fix_kind: "hreflang",
    mode: typeof p.mode === "string" ? p.mode : "replace",
    reason_code: typeof p.reason_code === "string" ? p.reason_code : undefined,
    before_entries: entries(p.before_entries),
    after_entries: entries(p.after_entries),
    changes,
    reason_codes: codeList(p.reason_codes),
    outstanding_reason_codes: codeList(p.outstanding_reason_codes),
    outstanding_note:
      typeof p.outstanding_note === "string" && p.outstanding_note.trim()
        ? p.outstanding_note
        : null,
    cluster: p.cluster === true ? true : undefined,
    pages,
    evidence,
  };
};

// ---------------------------------------------------------------------------
// missing_from_sitemap (fix_kind "sitemap")
//
// The fix is CAUSE-DEPENDENT, so the preview endpoint diagnoses WHY the page is
// absent and returns a decision:
//   auto_apply   — safe deterministic write (stale cache / page noindex)
//   needs_confirm— a human deliberately excluded it (plugin option) or the whole
//                  post type is excluded site-wide; never applied without an
//                  explicit confirmation (see confirm_site_wide on the apply)
//   refuse       — the exclusion lives in theme/plugin CODE (a filter) or the
//                  page legitimately can't be in a sitemap (draft, password…)
//   not_an_issue — false positive, so the row is skipped, not applied. TWO
//                  causes reach it: `front_or_blog_page` (SEO plugins list the
//                  homepage in a separate sitemap) and `already_in_sitemap` (the
//                  live sitemap DOES list this URL — the crawl read a stale or
//                  incomplete sitemap, so the stored row is simply out of date)
// `is_auto_fixable` on the preview is (decision === "auto_apply"), so a
// needs_confirm row is deliberately NOT bulk-applyable.
export type SitemapFixDecision =
  | "auto_apply"
  | "needs_confirm"
  | "refuse"
  | "not_an_issue";

export type SitemapSeoPlugin =
  | "yoast"
  | "rankmath"
  | "aioseo"
  | "core"
  | "none";

// How far a cause reaches: "page" = this URL only, "site" = every page of that
// post type (the blast-radius case), "none"/"unknown" = no server-side cause.
export type SitemapCauseScope = "page" | "site" | "none" | "unknown";

// The theme/plugin code the scanner blames for a filter-based exclusion. This is
// the highest-value output of the whole check: the exact file:line to delete.
export interface SitemapBlamedRef {
  file: string;
  line: number;
  priority: number;
}

export interface SitemapCauseDetail {
  code: string;
  scope: SitemapCauseScope;
  detail: string;
  fix?: string;
  blamed?: SitemapBlamedRef[];
  // Optional passthrough: the post type a site-wide exclusion applies to
  // (post_type_excluded / post_type_not_public), when the backend names it.
  post_type?: string;
  /** Can a force-include override beat this filter? True only for array/entry
   *  filter causes. NEVER true for sql_filter_present — that post is dropped from
   *  the sitemap's SQL query itself, so no later filter can add it back. The
   *  BACKEND is the authority (it whitelists the codes AND checks the preflight
   *  blockers); the card reads `override_available` on the plan, not this. */
  overridable?: boolean;
}

export interface SitemapFixPayload {
  fix_kind: "sitemap";
  decision: SitemapFixDecision;
  seo_plugin: SitemapSeoPlugin;
  sitemap_index_url: string;
  blocking_cause: SitemapCauseDetail;
  all_causes: SitemapCauseDetail[];
  /** post_type_excluded only: how many published posts of that type would be
   *  added to the sitemap AND made indexable if the exclusion is lifted. */
  blast_radius?: number;
  refusal_reason?: string;
  post_type?: string;
  /** The FORCE-INCLUDE OVERRIDE is on offer (decision is then needs_confirm):
   *  a theme/plugin filter excludes the page, and we can outrank it with our own
   *  PHP_INT_MAX callback WITHOUT editing their code. Backend-decided; it is
   *  false whenever a preflight blocker is present. */
  override_available?: boolean;
  /** This page is ALREADY force-included by us (Revert removes the override). */
  force_included?: boolean;
  /** Always true when the plan offers the override — a permanent reminder for the
   *  UI that the override masks the symptom and leaves their filter in place. */
  override_is_symptom_mask?: boolean;
  /** Why the override is NOT on offer, even though the cause is a beatable
   *  filter: our own detector is wrong about the page. `404_template` is the one
   *  that matters most — the 404page plugin uses a real, published, indexable
   *  page as the site's 404 template and excludes it with exactly this filter, so
   *  force-including it would submit the client's ERROR PAGE to Google. */
  preflight_blockers?: string[];
  /** The backend's LIVE sitemap pre-check said the URL IS listed — the crawl-time
   *  evidence on the row (`issue_details.in_sitemap`) is stale. Decision is then
   *  `not_an_issue` / cause `already_in_sitemap`, and the card must never claim
   *  "not listed in the sitemap". */
  already_in_sitemap?: boolean;
  /** The raw membership probe. `checked: false` means UNKNOWN (unreadable sitemap),
   *  NOT absent — never render "not in the sitemap" off it. */
  sitemap_membership?: SitemapMembership | null;
  /** Normalized conveniences for the card (read from `sitemap_membership`, then
   *  the blocking cause, then the plan): the sitemap we actually read, and the
   *  exact <loc> we matched in it. */
  sitemap_url?: string;
  matched_loc?: string;
}

/** backend `lookup_sitemap_membership()` — the live sitemap probe. */
export interface SitemapMembership {
  /** Did we actually READ a sitemap? False = UNKNOWN (never "absent"). */
  checked: boolean;
  /** Only meaningful when `checked`. */
  present: boolean;
  matched_loc?: string;
  url_count?: number;
  sitemap_index_url?: string;
  page_url?: string;
  reason?: string;
}

// Cause codes that mean "this was never an issue" (decision `not_an_issue`).
// `already_in_sitemap` is the one that can arrive on a row whose stored evidence
// says otherwise: trust the live re-check, not the crawl.
export const SITEMAP_NOT_AN_ISSUE_CAUSES = new Set<string>([
  "front_or_blog_page",
  "already_in_sitemap",
]);

// Cause codes that require a deliberate human confirmation before Apply writes.
// (The force-include override is confirm-gated too, but by `override_available`
// on the plan rather than by its cause code — the backend decides.)
export const SITEMAP_CONFIRM_CAUSES = new Set<string>([
  "excluded_by_option",
  "post_type_excluded",
]);

// Cause codes whose exclusion is site-wide: applying them changes EVERY page of
// that post type, so the apply body must carry `confirm_site_wide: true`.
export const SITEMAP_SITE_WIDE_CAUSES = new Set<string>(["post_type_excluded"]);

// The filter causes a force-include override can beat. Display-only (the badge /
// copy branch) — whether the override is actually OFFERED is `override_available`
// on the plan, which the backend computes.
export const SITEMAP_OVERRIDABLE_CAUSES = new Set<string>([
  "excluded_by_filter",
  "entry_filtered_out",
]);

// Human copy for a withheld override, keyed by the backend's blocker code. The
// backend normalizes the plugin's `preflight_` prefix away, so these are the bare
// codes. An unknown code still renders (generically) rather than vanishing.
export const SITEMAP_BLOCKER_COPY: Record<string, string> = {
  noindex:
    "this page is set to noindex, so submitting it would create a brand-new Search Console error (“Submitted URL marked ‘noindex’”) — worse than it simply being absent",
  robots_txt_blocked:
    "your robots.txt blocks this URL, so submitting it in the sitemap contradicts your own crawl rules",
  "404_template":
    "this page is your site's 404 error template (the 404page plugin uses a real, published page for it and excludes it from the sitemap with exactly this filter). Submitting it would hand Google your error page as a real destination",
  redirects:
    "this URL redirects somewhere else, and a sitemap should only list final destinations",
  not_200: "this URL doesn't return HTTP 200, so it isn't a live destination",
  not_self_canonical:
    "this page canonicalises to a different URL, so it is a duplicate — the canonical target is what belongs in the sitemap",
  woocommerce_page:
    "this is a WooCommerce utility page (cart / checkout / my account), which is never meant to be submitted to search",
  front_page:
    "this is your front page, which your SEO plugin lists separately — it isn't actually missing",
  blog_page:
    "this is your blog index page, which your SEO plugin lists separately — it isn't actually missing",
  external_permalink:
    "this page's permalink points to an external URL, so there is nothing on your site to submit",
  unverifiable:
    "we could not verify the page's live state, and we don't force-include a page we can't check",
};

const asSitemapBlamed = (value: unknown): SitemapBlamedRef | null => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const v = value as Record<string, unknown>;
  if (typeof v.file !== "string" || !v.file) return null;
  return {
    file: v.file,
    line: typeof v.line === "number" ? v.line : 0,
    priority: typeof v.priority === "number" ? v.priority : 0,
  };
};

const asSitemapCause = (value: unknown): SitemapCauseDetail | null => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const v = value as Record<string, unknown>;
  if (typeof v.code !== "string" || !v.code) return null;
  const blamed = Array.isArray(v.blamed)
    ? (v.blamed as unknown[])
        .map(asSitemapBlamed)
        .filter((b): b is SitemapBlamedRef => b !== null)
    : undefined;
  const scope: SitemapCauseScope =
    v.scope === "page" || v.scope === "site" || v.scope === "none"
      ? v.scope
      : "unknown";
  return {
    code: v.code,
    scope,
    detail: typeof v.detail === "string" ? v.detail : "",
    fix: typeof v.fix === "string" && v.fix.trim() ? v.fix : undefined,
    blamed: blamed && blamed.length > 0 ? blamed : undefined,
    post_type:
      typeof v.post_type === "string" && v.post_type.trim()
        ? v.post_type
        : undefined,
    overridable: v.overridable === true,
  };
};

const SITEMAP_DECISIONS = new Set<string>([
  "auto_apply",
  "needs_confirm",
  "refuse",
  "not_an_issue",
]);

const SITEMAP_PLUGINS = new Set<string>([
  "yoast",
  "rankmath",
  "aioseo",
  "core",
  "none",
]);

const strOrUndef = (value: unknown): string | undefined =>
  typeof value === "string" && value.trim() ? value : undefined;

// The live membership probe (backend `lookup_sitemap_membership`). `checked` is
// the important one: false = we could NOT read the sitemap, so membership is
// UNKNOWN — never absent.
const asSitemapMembership = (value: unknown): SitemapMembership | null => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const v = value as Record<string, unknown>;
  return {
    checked: v.checked === true,
    present: v.present === true,
    matched_loc: strOrUndef(v.matched_loc),
    url_count: typeof v.url_count === "number" ? v.url_count : undefined,
    sitemap_index_url: strOrUndef(v.sitemap_index_url),
    page_url: strOrUndef(v.page_url),
    reason: strOrUndef(v.reason),
  };
};

// Narrow an open `planned_payload` to the diagnosed sitemap plan. Returns null
// unless the payload really is a sitemap plan (fix_kind === "sitemap"), so
// legacy evidence-only rows fall back to the advisory rendering. An unrecognized
// decision degrades to "refuse" — the safe direction (never invent an apply).
export const readSitemapPreview = (
  payload: Record<string, unknown> | null | undefined,
): SitemapFixPayload | null => {
  const p = (payload ?? {}) as Record<string, unknown>;
  if (p.fix_kind !== "sitemap") return null;
  const causes = Array.isArray(p.all_causes)
    ? (p.all_causes as unknown[])
        .map(asSitemapCause)
        .filter((c): c is SitemapCauseDetail => c !== null)
    : [];
  const blocking =
    asSitemapCause(p.blocking_cause) ??
    causes[0] ?? {
      code: "unknown",
      scope: "unknown" as SitemapCauseScope,
      detail: "",
    };
  // The synthetic `already_in_sitemap` cause carries the matched loc / sitemap it
  // was matched in; asSitemapCause() drops unknown keys, so read them from raw.
  const rawCause =
    p.blocking_cause && typeof p.blocking_cause === "object"
      ? (p.blocking_cause as Record<string, unknown>)
      : {};
  const membership = asSitemapMembership(p.sitemap_membership);
  return {
    fix_kind: "sitemap",
    decision:
      typeof p.decision === "string" && SITEMAP_DECISIONS.has(p.decision)
        ? (p.decision as SitemapFixDecision)
        : "refuse",
    seo_plugin:
      typeof p.seo_plugin === "string" && SITEMAP_PLUGINS.has(p.seo_plugin)
        ? (p.seo_plugin as SitemapSeoPlugin)
        : "none",
    sitemap_index_url:
      typeof p.sitemap_index_url === "string" ? p.sitemap_index_url : "",
    blocking_cause: blocking,
    all_causes: causes,
    blast_radius:
      typeof p.blast_radius === "number" && p.blast_radius >= 0
        ? p.blast_radius
        : undefined,
    refusal_reason:
      typeof p.refusal_reason === "string" && p.refusal_reason.trim()
        ? p.refusal_reason
        : undefined,
    post_type:
      typeof p.post_type === "string" && p.post_type.trim()
        ? p.post_type
        : undefined,
    // Both default FALSE: a legacy payload (or a backend that didn't say) must
    // never look like an override is on offer, and never like one is in place.
    override_available: p.override_available === true,
    force_included: p.force_included === true,
    override_is_symptom_mask: p.override_is_symptom_mask === true,
    preflight_blockers: Array.isArray(p.preflight_blockers)
      ? (p.preflight_blockers as unknown[]).filter(
          (b): b is string => typeof b === "string" && b.trim() !== "",
        )
      : [],
    // Live sitemap membership. TRUE only when the backend explicitly says so (its
    // `already_in_sitemap` flag, or the cause code it derives it from) — a legacy
    // payload that says nothing must keep behaving as "missing", which is the
    // reason the row exists in the first place.
    already_in_sitemap:
      p.already_in_sitemap === true || blocking.code === "already_in_sitemap",
    sitemap_membership: membership,
    // The sitemap we actually read, and the entry we matched in it. Both live on
    // the membership probe; the synthetic `already_in_sitemap` cause carries the
    // same values, so fall back to it (and finally to the index URL).
    sitemap_url:
      membership?.sitemap_index_url ||
      strOrUndef(rawCause.sitemap_index_url) ||
      (typeof p.sitemap_index_url === "string" ? p.sitemap_index_url : undefined),
    matched_loc:
      membership?.matched_loc || strOrUndef(rawCause.matched_loc) || undefined,
  };
};

/** What the drawer needs to know to gate Apply for a missing_from_sitemap row.
 *  `canApply` is false until a needs_confirm row is explicitly confirmed, and
 *  false forever for refuse / not_an_issue. `confirmSiteWide` is what flows into
 *  the apply body as `confirm_site_wide` — it is ONLY ever true after the user
 *  ticks a confirmation.
 *
 *  ONE flag, both confirmable paths (deliberately — no second `confirm_override`):
 *  `confirm_site_wide` is the request's "the human ticked the box for THIS row"
 *  token. Which write it authorises is decided by the backend's LIVE diagnosis
 *  (un-exclude the post type, or register the force-include override), never by
 *  the caller. Bulk apply sends no body, so neither can be swept in by "fix all". */
export interface SitemapApplyGate {
  decision: SitemapFixDecision | null;
  canApply: boolean;
  confirmSiteWide: boolean;
}

const LARGE_IMAGE_RESULT_STATES = new Set<LargeImageResultState>([
  "applied",
  "skipped",
  "protected",
  "failed",
]);

const asLargeImageRecord = (value: unknown): Record<string, unknown> | null => {
  if (!value) return null;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return null;
    try {
      const parsed = JSON.parse(trimmed);
      return parsed && typeof parsed === "object" && !Array.isArray(parsed)
        ? (parsed as Record<string, unknown>)
        : null;
    } catch {
      return null;
    }
  }
  return typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
};

const isLargeImageResultLike = (record: Record<string, unknown>): boolean => {
  if (
    record.issue_type === "large_image" ||
    record.method === "image_optimize"
  ) {
    return true;
  }
  return [
    "original_size",
    "new_size",
    "bytes_saved",
    "new_dimensions",
    "still_over_max",
    "image_context",
  ].some((key) => Object.prototype.hasOwnProperty.call(record, key));
};

const getResultStatusValue = (
  result: LargeImageProofResult | null,
): string | null => {
  if (!result) return null;
  const direct = result.status || result.result_status;
  return typeof direct === "string" ? direct.toLowerCase() : null;
};

export const normalizeLargeImageResultState = (
  result: LargeImageProofResult | null | undefined,
): LargeImageResultState | null => {
  if (!result) return null;
  const direct = getResultStatusValue(result);
  if (
    direct &&
    LARGE_IMAGE_RESULT_STATES.has(direct as LargeImageResultState)
  ) {
    return direct as LargeImageResultState;
  }

  const message = String(
    result.error || result.message || result.reason || result.warning || "",
  ).toLowerCase();
  if (
    result.protected === true ||
    result.image_context === "page" ||
    message.includes("protected") ||
    message.includes("service page") ||
    message.includes("landing")
  ) {
    return "protected";
  }
  if (
    result.skipped === true ||
    result.method === "already_applied" ||
    message.includes("already") ||
    message.includes("skipped") ||
    message.includes("not reduced")
  ) {
    return "skipped";
  }
  if (
    result.success === true ||
    (typeof result.new_size === "number" && result.new_size > 0)
  ) {
    return "applied";
  }
  if (result.success === false || Boolean(result.error)) {
    return "failed";
  }
  return null;
};

export const readLargeImageResult = (
  issue: PageIssue | null | undefined,
): LargeImageProofResult | null => {
  if (!issue || issue.issue_type !== "large_image") return null;
  const bag = issue as unknown as Record<string, unknown>;
  const candidates = [
    bag.large_image_result,
    bag.largeImageResult,
    bag.proof_result,
    bag.proofResult,
    bag.proof,
    bag.apply_result,
    bag.applyResult,
    bag.fix_result,
    bag.fixResult,
    bag.result,
  ];

  for (const candidate of candidates) {
    const record = asLargeImageRecord(candidate);
    if (!record) continue;
    const nestedCandidates = [
      record,
      asLargeImageRecord(record.result),
      asLargeImageRecord(record.proof),
      asLargeImageRecord(record.large_image_result),
    ];
    for (const nested of nestedCandidates) {
      if (nested && isLargeImageResultLike(nested)) {
        return nested as LargeImageProofResult;
      }
    }
  }

  if (issue.error && issue.fix_status === "failed") {
    return {
      status: "failed",
      issue_type: "large_image",
      error: issue.error,
      success: false,
    };
  }

  return null;
};

export const getLargeImageResultState = (
  issue: PageIssue | null | undefined,
): LargeImageResultState | null =>
  normalizeLargeImageResultState(readLargeImageResult(issue));

// =============================================================================
// Severity Calculation Helper
// =============================================================================

export function calculateSeverity(issue: PageIssue): IssueSeverity {
  if (
    issue.severity === "critical" ||
    issue.severity === "warning" ||
    issue.severity === "info"
  ) {
    return issue.severity;
  }

  switch (issue.issue_type) {
    case "title_meta": {
      const details = issue.issue_details as TitleMetaDetails;
      if (details.title_missing || details.meta_missing) return "critical";
      if (details.title_too_long || details.meta_too_long) return "warning";
      return "info";
    }
    // Atomic split of title_meta. Mirrors the combined case's bands: a missing
    // title/meta is critical, over-length is a warning, under-length is info.
    case "page_title_missing":
    case "meta_missing":
      return "critical";
    case "page_title_over_60_chars":
    case "meta_description_over_155_chars":
      return "warning";
    case "page_title_same_as_h1":
    case "page_title_below_30_chars":
    case "meta_description_below_70_chars":
      return "info";
    // Atomic split of the legacy lumped "h1" type. Mirrors the combined case's
    // bands: missing H1 is critical, multiple H1s is a warning, overlong is info.
    case "h1_missing":
      return "critical";
    case "h1_multiple":
      return "warning";
    case "h1_too_long":
      return "info";
    case "h1": {
      const details = issue.issue_details as H1Details;
      if (details.h1_missing) return "critical";
      if (details.multiple_h1s) return "warning";
      return "info";
    }
    case "alt_text":
      return "warning";
    case "redirect": {
      const details = issue.issue_details as RedirectDetails;
      if (details.redirect_count > 3) return "critical";
      if (details.redirect_count > 1) return "warning";
      return "info";
    }
    case "broken_link": {
      const details = issue.issue_details as BrokenLinkDetails;
      if ([404, 410, 500, 502, 503].includes(details.status_code))
        return "critical";
      return "warning";
    }
    case "broken_internal":
      return "critical";
    case "canonical": {
      const details = issue.issue_details as CanonicalDetails;
      if (getCanonicalIssueSubtype(details) === "missing") return "critical";
      return "warning";
    }
    case "duplicate_title":
      return "warning";
    case "duplicate_meta":
      return "warning";
    case "thin_content": {
      const details = issue.issue_details as ThinContentDetails;
      if (details.word_count < 100) return "critical";
      return "warning";
    }
    case "indexability":
      return "critical";
    // A noindex on a proven-important page hides it from Google entirely.
    case "noindex_important":
      return "critical";
    case "slow_response": {
      const details = issue.issue_details as SlowResponseDetails;
      if (details.response_time_ms > 5000) return "critical";
      return "warning";
    }
    case "near_duplicate": {
      const details = issue.issue_details as NearDuplicateDetails;
      if (details.similarity_score > 0.95) return "critical";
      return "warning";
    }
    case "orphan_page":
      return "warning";
    case "schema_error":
      return "warning";
    case "missing_schema":
      return "warning";
    case "schema_validation_error": {
      const details = issue.issue_details as SchemaValidationDetails;
      return (details?.error_count ?? 0) >= 3 ? "critical" : "warning";
    }
    case "missing_og_tags":
      return "warning";
    case "mixed_content":
      return "warning";
    case "redirect_loop":
      return "critical";
    case "title_too_short":
      return "warning";
    case "serp_title_truncated":
      return "warning";
    case "serp_meta_truncated":
      return "info";
    case "multiple_meta":
      return "warning";
    case "duplicate_h1":
      return "warning";
    case "low_readability":
      return "info";
    case "low_text_ratio":
      return "info";
    case "stale_content":
      return "info";
    case "broken_external": {
      const details = issue.issue_details as BrokenExternalDetails;
      if ([404, 410, 500, 502, 503].includes(details.status_code))
        return "critical";
      return "warning";
    }
    // Source-page-anchored (one row lists N broken internal targets). High
    // severity like broken_internal — a page linking to dead internal pages is a
    // hard UX/crawl problem regardless of the individual status codes.
    case "links_to_broken":
      return "critical";
    case "deep_page": {
      const details = issue.issue_details as DeepPageDetails;
      if (details.click_depth > 5) return "critical";
      return "warning";
    }
    case "large_image": {
      const details = issue.issue_details as LargeImageDetails;
      if (details.file_size_bytes > 2_000_000) return "critical";
      return "warning";
    }
    case "broken_image":
      return "critical";
    case "non_nextgen_image":
      return "info";
    case "large_page": {
      const details = issue.issue_details as LargePageDetails;
      if (details.page_size_bytes > 5_000_000) return "critical";
      return "warning";
    }
    case "unsafe_crossorigin":
      return "info";
    case "nofollow_internal":
      return "warning";
    case "low_internal_links":
      return "warning";
    case "missing_hsts":
      return "info";
    case "hreflang_error":
      return "warning";
    case "sitemap_error":
      return "warning";
    // medium severity -> warning (Google can still reach the page via internal
    // links; it just isn't explicitly submitted).
    case "missing_from_sitemap":
      return "warning";
    case "performance_diagnostics":
      return "warning";
    case "misspelling":
      return "info";
    case "render_blocking":
      return "warning";
    case "links_to_redirect":
      return "warning";
    case "missing_compression":
      return "info";
    case "bad_url_structure":
      return "info";
    case "has_iframe":
      return "info";
    case "keyword_cannibalization":
      return "warning";
    case "image_missing_dimensions":
      return "info";
    case "core_web_vitals":
      return "warning";
  }
}

// =============================================================================
// UI Helper Types
// =============================================================================

export const ISSUE_TYPE_LABELS: Record<IssueType, string> = {
  title_meta: "Title/Meta",
  h1: "H1",
  h1_missing: "Missing H1",
  h1_multiple: "Multiple H1 Tags",
  h1_too_long: "H1 Too Long",
  alt_text: "Image Alt Text",
  redirect: "Redirect",
  broken_link: "Broken Link",
  broken_internal: "Broken Internal URL",
  canonical: "Canonicals",
  duplicate_title: "Duplicate SEO Title Tags",
  page_title_same_as_h1: "Title Matches H1",
  page_title_missing: "Missing SEO Title",
  page_title_over_60_chars: "SEO Title Too Long",
  page_title_below_30_chars: "SEO Title Too Short",
  duplicate_meta: "Duplicate Descriptions",
  meta_missing: "Missing Meta Description",
  meta_description_over_155_chars: "Meta Description Too Long",
  meta_description_below_70_chars: "Meta Description Too Short",
  thin_content: "Thin Content",
  indexability: "Indexability",
  noindex_important: "Noindex on Important Page",
  slow_response: "Slow Response",
  near_duplicate: "Near-Duplicate Content",
  orphan_page: "Orphan Page",
  schema_error: "Schema Validation Error",
  missing_schema: "Missing Schema",
  schema_validation_error: "Schema Validation Errors",
  missing_og_tags: "Missing Open Graph Tags",
  mixed_content: "Mixed Content",
  redirect_loop: "Redirect Loop",
  title_too_short: "Title Too Short",
  serp_title_truncated: "SERP Title Truncated",
  serp_meta_truncated: "SERP Description Truncated",
  multiple_meta: "Multiple Meta Descriptions",
  duplicate_h1: "Duplicate H1",
  low_readability: "Low Readability",
  low_text_ratio: "Low Text-to-HTML Ratio",
  stale_content: "Stale Content",
  broken_external: "Broken External Link",
  deep_page: "Deep Page",
  large_image: "Oversized Image",
  broken_image: "Broken Image",
  non_nextgen_image: "Non-Next-Gen Image",
  large_page: "Oversized Page",
  unsafe_crossorigin: "Unsafe Cross-Origin Link",
  nofollow_internal: "Nofollow Internal Link",
  low_internal_links: "Low Internal Links",
  missing_hsts: "Missing HSTS",
  hreflang_error: "Hreflang Error",
  sitemap_error: "Sitemap Issue",
  missing_from_sitemap: "Missing from Sitemap",
  performance_diagnostics: "Performance Diagnostics",
  misspelling: "Spelling Errors",
  render_blocking: "Render-Blocking Resources",
  links_to_redirect: "Links to Redirects",
  links_to_broken: "Links to Broken Page",
  missing_compression: "Missing Compression",
  bad_url_structure: "Non-SEO-Friendly URL",
  has_iframe: "Contains iFrame",
  https_to_http: "HTTPS to HTTP Link",
  redirect_chain: "Redirect Chain",
  broken_resource: "Broken Resource",
  canonical_to_broken: "Canonical Points to Broken",
  canonical_to_redirect: "Canonical Points to Redirect",
  canonical_chain: "Canonical Chain",
  keyword_cannibalization: "Keyword Cannibalization",
  image_missing_dimensions: "Image Missing Dimensions",
  core_web_vitals: "Core Web Vitals",
};

type IssueLabelInput =
  | IssueType
  | {
      issue_type: IssueType | string;
      issue_details?: unknown;
    };

const asIssueDetailRecord = (
  details: unknown,
): Record<string, unknown> | null =>
  details && typeof details === "object" && !Array.isArray(details)
    ? (details as Record<string, unknown>)
    : null;

const hasDetailFlag = (
  details: Record<string, unknown> | null | undefined,
  key: string,
): boolean => details?.[key] === true;

/**
 * True when a row represents a MISSING H1, whichever shape the detector used:
 * the atomic issue_type "h1_missing", or the legacy composite "h1" carrying an
 * issue_details.h1_missing flag (or h1_count === 0). The list filter, the row
 * label ("Missing H1"), and the severity logic all already treat the composite
 * shape as missing-H1; any UI gate that checks issue_type === "h1_missing"
 * alone silently skips those rows. Coercions deliberately mirror IssueTable's
 * truthyDetailFlag/detailNumber (loose string flags, parsed counts) so the
 * drawer and the filter can never disagree about what "Missing H1" means.
 */
export function isMissingH1Issue(
  issue: Pick<PageIssue, "issue_type" | "issue_details">,
): boolean {
  if (issue.issue_type === "h1_missing") return true;
  if (issue.issue_type !== "h1") return false;
  const details = asIssueDetailRecord(issue.issue_details);
  const flag = details?.["h1_missing"];
  const flagTruthy =
    typeof flag === "boolean"
      ? flag
      : typeof flag === "string"
        ? ["true", "1", "yes"].includes(flag.trim().toLowerCase())
        : Boolean(flag);
  if (flagTruthy) return true;
  const count = details?.["h1_count"];
  const parsed =
    typeof count === "number" && Number.isFinite(count)
      ? count
      : typeof count === "string"
        ? Number.parseInt(count.trim(), 10)
        : Number.NaN;
  return parsed === 0;
}

export function getCanonicalIssueSubtype(
  details: Record<string, unknown> | null | undefined,
): "missing" | "multiple" | "mismatch" {
  const explicit = details?.issue_type;
  if (
    explicit === "missing" ||
    explicit === "multiple" ||
    explicit === "mismatch"
  ) {
    return explicit;
  }
  if (hasDetailFlag(details, "multiple_canonicals")) return "multiple";
  if (hasDetailFlag(details, "canonical_mismatch")) return "mismatch";
  return "missing";
}

export function getIssueTypeLabel(input: IssueLabelInput): string {
  const issueType = typeof input === "string" ? input : input.issue_type;
  const details =
    typeof input === "string"
      ? undefined
      : asIssueDetailRecord(input.issue_details);

  if (issueType === "title_meta") {
    const hasTitleIssue =
      hasDetailFlag(details, "title_missing") ||
      hasDetailFlag(details, "title_too_long") ||
      hasDetailFlag(details, "title_too_short");

    if (!hasTitleIssue) {
      if (hasDetailFlag(details, "meta_missing"))
        return "Missing Meta Description";
      if (hasDetailFlag(details, "meta_too_long"))
        return "Meta Description Too Long";
      if (hasDetailFlag(details, "meta_too_short"))
        return "Meta Description Too Short";
    }
  }

  if (issueType === "h1") {
    if (hasDetailFlag(details, "h1_missing")) return "Missing H1";
    if (hasDetailFlag(details, "multiple_h1s")) return "Multiple H1 Tags";
    if (hasDetailFlag(details, "h1_too_long")) return "H1 Too Long";
  }

  if (issueType === "canonical") {
    const subtype = getCanonicalIssueSubtype(details);
    if (subtype === "missing") return "Missing Canonical";
    if (subtype === "multiple") return "Multiple Canonicals";
    if (subtype === "mismatch") return "Canonical Mismatch";
  }

  return (
    ISSUE_TYPE_LABELS[issueType as IssueType] || String(issueType || "Issue")
  );
}

export function getIssueTypeFilterLabel(issueType: IssueType | string): string {
  if (issueType === "h1") return "Missing H1 / Multiple H1 Tags";
  return (
    ISSUE_TYPE_LABELS[issueType as IssueType] || String(issueType || "Issue")
  );
}

const ISSUE_TYPE_FILTER_SEARCH_ALIASES: Partial<Record<IssueType, string>> = {
  h1: "Missing H1 Multiple H1 Tags H1 Heading",
  h1_missing: "Missing H1 No H1 Heading",
  h1_multiple: "Multiple H1 Tags More Than One H1",
  h1_too_long: "H1 Too Long Overlong Heading",
  title_meta:
    "Missing Meta Description Meta Description Missing Title Title Meta",
  page_title_same_as_h1: "SEO Title Matches H1 Title Same As H1",
  page_title_missing: "Missing SEO Title Missing Page Title Missing Title Tag",
  page_title_over_60_chars: "SEO Title Too Long Page Title Over 60 Characters",
  page_title_below_30_chars:
    "SEO Title Too Short Page Title Below 30 Characters Short Title",
  meta_missing: "Missing Meta Description No Description",
  meta_description_over_155_chars:
    "Meta Description Too Long Over 155 Characters",
  meta_description_below_70_chars:
    "Meta Description Too Short Below 70 Characters",
  meta_description_multiple: "Multiple Meta Descriptions Duplicate Meta Tags",
  links_to_broken:
    "Links to Broken Page Links to Broken Internal Pages Dead Internal Links 404 Broken Destinations",
  noindex_important:
    "Noindex on Important Page Noindexed Page Blocked From Google Robots Meta Index",
};

export function getIssueTypeFilterSearchValue(
  issueType: IssueType | string,
): string {
  return [
    getIssueTypeFilterLabel(issueType),
    issueType,
    ISSUE_TYPE_FILTER_SEARCH_ALIASES[issueType as IssueType] || "",
  ]
    .filter(Boolean)
    .join(" ");
}

export const SEVERITY_LABELS: Record<IssueSeverity, string> = {
  critical: "Critical",
  warning: "Warning",
  info: "Info",
};

export const STATUS_LABELS: Record<IssueStatus, string> = {
  pending: "Pending",
  approved: "Suggested",
  rejected: "Skipped",
  applied: "Applied",
  recommended: "Recommendation saved",
  verified: "Verified",
  failed: "Failed",
  skipped: "Skipped",
  protected: "Protected",
  obsolete: "Obsolete",
  manual: "Expert fix requested",
};

// =============================================================================
// Crawl Configuration & Status Types
// =============================================================================

export interface CrawlConfig {
  website: string;
  tenant_id: string;
  /** Bypass the dedup guard and force a fresh crawl even if a recent one exists. */
  force?: boolean;

  // Content type filters
  include_pages: boolean;
  include_posts: boolean;
  include_products: boolean;
  custom_post_types: string[];

  // URL filters
  include_patterns: string[];
  exclude_patterns: string[];

  // Date filters
  date_filter: "all" | "recent" | "range" | null;
  days_recent: number;
  date_from: string | null;
  date_to: string | null;

  // Issue types (original)
  check_title_meta: boolean;
  check_h1: boolean;
  check_alt_text: boolean;
  check_redirects: boolean;
  check_broken_links: boolean;
  check_canonicals: boolean;
  check_duplicate_titles: boolean;
  check_duplicate_meta: boolean;
  check_thin_content: boolean;
  check_indexability: boolean;
  check_slow_response: boolean;

  // Issue types (new)
  check_near_duplicate: boolean;
  check_orphan_page: boolean;
  check_schema_error: boolean;
  check_mixed_content: boolean;
  check_redirect_loop: boolean;
  check_title_too_short: boolean;
  check_serp_title_truncated: boolean;
  check_serp_meta_truncated: boolean;
  check_multiple_meta: boolean;
  check_duplicate_h1: boolean;
  check_low_readability: boolean;
  check_low_text_ratio: boolean;
  check_stale_content: boolean;
  check_broken_external: boolean;
  check_deep_page: boolean;
  check_large_image: boolean;
  check_non_nextgen_image: boolean;
  check_large_page: boolean;
  check_unsafe_crossorigin: boolean;
  check_nofollow_internal: boolean;
  check_low_internal_links: boolean;

  // Issue types (DFS-only)
  check_misspelling: boolean;
  check_render_blocking: boolean;
  check_links_to_redirect: boolean;
  check_links_to_broken: boolean;
  check_missing_compression: boolean;
  check_bad_url_structure: boolean;
  check_has_iframe: boolean;
}

export interface AuditRunSummary {
  id: string;
  tenant_id: string;
  website: string;
  status: "pending" | "running" | "completed" | "failed";
  total_issues: number;
  pages_crawled?: number;
  issues_by_severity: Record<string, number>;
  issues_by_status: Record<string, number>;
  started_at: string;
  completed_at: string | null;
  crawl_config: CrawlConfig | null;
}

export interface CrawlStatus {
  crawl_id: string;
  status: "pending" | "running" | "completed" | "failed";
  progress_percent: number;
  pages_crawled: number;
  total_pages: number;
  issues_found: number;
  current_url: string | null;
  elapsed_seconds: number;
  error_message: string | null;
}

export interface StartCrawlResponse {
  crawl_id: string;
  run_id: string;
  status: string;
  message: string;
  /** True when the backend reused a recent/in-flight run instead of crawling again. */
  deduped?: boolean;
  started_at?: string;
}
